import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { Liga, ResultadoCarrera, Socio, Carrera } from '../../models/types';
import { FaTrophy, FaFlag, FaStopwatch, FaCar, FaExclamationTriangle } from 'react-icons/fa';
import LastreInfo from '../../components/LastreInfo';
import { calcularLastresLiga } from '../../utils/lastreCalculator';
import { theme } from '../../theme';

// Responsive breakpoints
const breakpoints = {
  mobile: '768px',
  tablet: '1024px'
};

// Define la interfaz para los resultados calculados de los pilotos
interface ResultadoPilotoCalculado {
  socioId: string;
  nombreCompleto: string;
  fotoUrl?: string;
  equipoId?: string;
  equipoNombre?: string;
  equipoLogoUrl?: string;
  puntosTotales: number;
  victorias: number;
  segundosPuestos: number;
  tercerosPuestos: number;
  poles: number;
  vueltasRapidas: number;
  carrerasParticipadas: number;
  sancionesPuntosTotales?: number;
  puntosLicencia?: string | number;
  nivelReputacion?: string | number;
  historialCarrerasLiga?: {
    carreraId: string;
    nombreCarrera: string;
    posicion: number;
    puntosObtenidos?: number;
    hizoPole?: boolean;
    hizoVueltaRapida?: boolean;
  }[];
}

// Helper function to get initials from a name
const getInitials = (name: string): string => {
  if (!name) return '??';
  const nameParts = name.trim().split(' ').filter(part => part.length > 0);
  if (nameParts.length === 0) return '??';
  if (nameParts.length === 1) {
    return nameParts[0].substring(0, 2).toUpperCase();
  }
  return (nameParts[0][0] + (nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : '')).toUpperCase();
};

// Helper function to generate SVG data URI for initials avatar
const generateInitialsAvatar = (name: string): string => {
  const initials = getInitials(name);
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = (name || '').charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }
  const hue = hash % 360;
  const backgroundColor = `hsl(${hue}, 70%, 60%)`;
  const textColor = `hsl(${hue}, 30%, 95%)`;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
      <rect width="100%" height="100%" fill="${backgroundColor}"/>
      <text x="50%" y="50%" dy=".1em" dominant-baseline="middle" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="40px" font-weight="bold" fill="${textColor}">
        ${initials}
      </text>
    </svg>
  `.trim().replace(/\n\s*/g, "");
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Placeholder SVG para el logo de equipo por defecto
const defaultTeamLogo = `data:image/svg+xml;base64,${btoa('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100"><rect width="100" height="100" fill="#cccccc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="40" fill="#555555">T</text></svg>')}`;

// Placeholder SVG para el avatar de usuario por defecto
const defaultAvatar = `data:image/svg+xml;base64,${btoa('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100"><circle cx="50" cy="50" r="45" fill="#dddddd"/><path d="M50 62.5c-12.4 0-22.5 10.1-22.5 22.5V90h45v-5c0-12.4-10.1-22.5-22.5-22.5zM50 20c-9.9 0-18 8.1-18 18s8.1 18 18 18 18-8.1 18-18-8.1-18-18-18z" fill="#aaaaaa"/></svg>')}`;

// Definiciones de temas locales (expandidas)
const darkThemeColors = {
  // Base y Texto - Fondos oscuros (azul corporativo), texto claro
  backgroundPage: '#0a0c19',      // theme.colors.primary.dark (Azul corporativo muy oscuro)
  textPrimary: '#FFFFFF',         // theme.colors.primary.contrastText
  textSecondary: '#b0b0c0',       // Gris claro para texto secundario
  textMuted: '#888e9b',         // Gris más oscuro para texto silenciado
  textLink: '#ff6259',            // Rojo corporativo claro para enlaces (theme.colors.secondary.light)
  titleColor: '#FFFFFF',          // Títulos principales en blanco
  buttonBackground: '#e4312e',    // Rojo corporativo para botones principales (theme.colors.secondary.main)
  buttonText: '#FFFFFF',          // theme.colors.secondary.contrastText
  avatarBackground: '#1e2340',    // Azul corporativo claro para fondo de avatares (theme.colors.primary.light)

  // Podium - Fondos oscuros azulados, acentos en rojo y colores de medallas
  podiumBackground: 'rgba(30, 35, 64, 0.3)', // Azul corporativo claro semi-transparente (derivado de #1e2340)
  podiumBorder: 'rgba(228, 49, 46, 0.3)',  // Borde rojo corporativo sutil (derivado de #e4312e)
  pilotImageBorder: '#e4312e',    // Rojo corporativo para borde de imagen de piloto
  statsBackground: 'rgba(30, 35, 64, 0.2)', // Azul corporativo claro semi-transparente
  textStats: '#e0e0e0',           // Texto de estadísticas en gris muy claro
  gold: '#FFD700',                // Mantenemos colores estándar de medallas
  silver: '#C0C0C0',
  bronze: '#CD7F32',

  // Lista de Clasificación - Fondos azulados oscuros, texto claro, acentos rojos
  listTitleColor: '#FFFFFF',       // Título de la lista en blanco
  listItemBackground: '#101325',  // Azul corporativo (theme.colors.primary.main)
  listItemBorder: 'rgba(228, 49, 46, 0.2)', // Borde rojo corporativo más sutil
  listAvatarBorder: '#e4312e',    // Rojo corporativo para avatares en lista
  listTextPrimary: '#FFFFFF',
  listTextSecondary: '#b0b0c0',
  listTextMuted: '#888e9b',

  // Modal de Historial - Estilo oscuro corporativo
  modalOverlayBackground: 'rgba(10, 12, 25, 0.7)', // Overlay azul corporativo oscuro (theme.colors.primary.dark)
  modalContentBackground: '#101325', // Azul corporativo (theme.colors.primary.main)
  modalBorder: 'rgba(228, 49, 46, 0.3)', // Borde rojo corporativo sutil
  modalTitleColor: '#FFFFFF',      // Título de modal en blanco
  modalTextColor: '#e0e0e0',
  modalSubtleTextColor: '#b0b0c0',
  modalCloseButtonBackground: 'transparent',
  modalCloseButtonColor: '#b0b0c0',
  modalCloseButtonHoverBackground: '#e4312e', // Rojo corporativo al pasar el ratón
  modalCloseButtonHoverColor: '#FFFFFF',
  modalSectionBorder: 'rgba(228, 49, 46, 0.2)', // Borde de sección rojo sutil
  raceCardBackground: '#1e2340',   // Azul corporativo claro (theme.colors.primary.light)
  raceCardBorder: 'rgba(228, 49, 46, 0.2)',
  raceCardHeaderBackground: 'rgba(10, 12, 25, 0.5)', // Azul corporativo oscuro semi-transparente
  raceCardHeaderText: '#b0b0c0',
  teammateItemBackground: '#101325', // Azul corporativo para elementos de compañero
  teammateItemBorder: 'rgba(228, 49, 46, 0.15)',
};

const lightThemeColors = {
  // Base y Texto - Alineado con theme.ts
  backgroundPage: '#F5F7FA', // theme.colors.background.default
  textPrimary: '#101325',     // theme.colors.text.primary (Azul corporativo)
  textSecondary: '#5A6178',   // theme.colors.text.secondary
  textMuted: '#9DA3B4',       // theme.colors.text.disabled / hint
  textLink: '#101325',        // Azul corporativo para enlaces
  titleColor: '#101325',      // Azul corporativo para títulos principales
  buttonBackground: '#101325',// Azul corporativo para botones principales
  buttonText: '#FFFFFF',      // theme.colors.primary.contrastText
  avatarBackground: '#e0e0e0', // Un gris neutro claro para avatares

  // Podium - Fondos claros, bordes sutiles, colores corporativos para acentos
  podiumBackground: 'rgba(16, 19, 37, 0.03)', // Muy sutil, derivado del azul corporativo
  podiumBorder: 'rgba(16, 19, 37, 0.1)',     // Borde sutil azulado
  pilotImageBorder: '#101325', // Borde azul corporativo para la imagen del piloto
  statsBackground: 'rgba(16, 19, 37, 0.05)',
  textStats: '#101325',       // Texto de estadísticas en azul corporativo
  gold: '#FFD700',            // Mantenemos colores estándar de medallas
  silver: '#C0C0C0',
  bronze: '#CD7F32',

  // Lista de Clasificación - Fondos blancos, texto oscuro, acentos corporativos
  listTitleColor: '#101325',   // Título de la lista en azul corporativo
  listItemBackground: '#FFFFFF', // theme.colors.background.paper
  listItemBorder: 'rgba(16, 19, 37, 0.08)',  // Borde sutil azulado
  listAvatarBorder: '#e4312e', // Borde rojo corporativo para avatares en lista
  listTextPrimary: '#101325',
  listTextSecondary: '#5A6178',
  listTextMuted: '#9DA3B4',

  // Modal de Historial - Estilo claro y limpio
  modalOverlayBackground: 'rgba(16, 19, 37, 0.5)', // Overlay semi-transparente azulado
  modalContentBackground: '#FFFFFF', // theme.colors.background.paper
  modalBorder: 'rgba(16, 19, 37, 0.15)',
  modalTitleColor: '#101325',    // Título de modal en azul corporativo
  modalTextColor: '#101325',
  modalSubtleTextColor: '#5A6178',
  modalCloseButtonBackground: 'transparent',
  modalCloseButtonColor: '#5A6178',
  modalCloseButtonHoverBackground: '#e4312e', // Rojo corporativo al pasar el ratón (para acciones destructivas o cierre)
  modalCloseButtonHoverColor: '#FFFFFF',
  modalSectionBorder: 'rgba(16, 19, 37, 0.1)',
  raceCardBackground: '#F8F9FA', // Ligeramente distinto del fondo de página
  raceCardBorder: 'rgba(16, 19, 37, 0.1)',
  raceCardHeaderBackground: 'rgba(16, 19, 37, 0.03)',
  raceCardHeaderText: '#5A6178',
  teammateItemBackground: '#FFFFFF', // Elementos de compañero en blanco
  teammateItemBorder: 'rgba(16, 19, 37, 0.08)',
};

// Define la interfaz para los resultados calculados de los pilotos
interface ResultadoPilotoCalculado {
  socioId: string;
  nombreCompleto: string;
  fotoUrl?: string;
  equipoId?: string;
  equipoNombre?: string;
  equipoLogoUrl?: string;
  puntosTotales: number;
  victorias: number;
  segundosPuestos: number;
  tercerosPuestos: number;
  poles: number;
  vueltasRapidas: number;
  carrerasParticipadas: number;
  sancionesPuntosTotales?: number;
  puntosLicencia?: string | number;
  nivelReputacion?: string | number;
  historialCarrerasLiga?: {
    carreraId: string;
    nombreCarrera: string;
    posicion: number;
    puntosObtenidos?: number;
    hizoPole?: boolean;
    hizoVueltaRapida?: boolean;
  }[];
}



const ThemeToggleButton = styled.button`
  position: absolute; 
  top: 20px;
  right: 20px;
  padding: 10px 15px;
  background-color: ${props => props.theme.buttonBackground};
  color: ${props => props.theme.buttonText};
  border: none;
  border-radius: 5px;
  cursor: pointer;
  z-index: 2000; 
  font-size: 0.9rem;
  transition: background-color 0.3s ease, color 0.3s ease;

  &:hover {
    opacity: 0.9;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    top: 10px;
    right: 10px;
    padding: 8px 12px;
    font-size: 0.8rem;
  }
`;

const PodiumContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 60px;
  min-height: 600px;
  background-color: #0a0c19;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    min-height: auto;
    margin-bottom: 30px;
    padding: 20px 5px;
    width: 100%;
    box-sizing: border-box;
    background-color: #0a0c19;
  }
`;
const PageContainer = styled.div`
  padding: 0;
  margin: 0;
  color: ${props => props.theme.textPrimary};
  min-height: 100vh;
  transition: background-color 0.3s ease, color 0.3s ease;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #0a0c19;
  z-index: 1;
  
  @media (max-width: ${breakpoints.mobile}) {
    position: fixed;
    width: 100vw;
    height: 100vh;
    min-height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;
    box-sizing: border-box;
    padding-top: 60px; /* Compensar la altura del header móvil fijo */
    
    /* Eliminar cualquier espacio blanco */
    &::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: #0a0c19;
      z-index: -1;
    }
  }
`;

const Title = styled.h1`
  text-align: center;
  background-color: #0a0c19;
  color: ${props => props.theme.titleColor};
  margin: 20px 0 30px 0;
  padding-top: 20px;
  font-size: 2.2rem;
  font-weight: bold;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.8rem;
    margin: 10px 0 20px 0;
    padding-top: 15px;
    padding-left: 15px;
    padding-right: 15px;
  }
`;
const PodiumStep = styled.div<{ $position: number }>`
  background-color: ${props => props.theme.podiumBackground};
  border: 1px solid ${props => props.theme.podiumBorder};
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  width: 250px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  box-shadow: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease, height 0.3s ease;
  color: ${props => props.theme.textPrimary};
  height: ${props => props.$position === 1 ? '100%' : '95%'};
  margin-top: ${props => props.$position === 1 ? '0' : '2.5%'};
  
  /* Reordenar visualmente en desktop para mostrar podio correcto: 2º-1º-3º */
  order: ${props => {
    if (props.$position === 1) return 2; // Primer puesto en el centro
    if (props.$position === 2) return 1; // Segundo puesto a la izquierda  
    if (props.$position === 3) return 3; // Tercer puesto a la derecha
    return props.$position;
  }};

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px ${props => (props.$position === 1 ? props.theme.gold : props.$position === 2 ? props.theme.silver : props.theme.bronze)};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    width: calc(100% - 20px);
    max-width: 320px;
    height: auto !important;
    margin-top: 0 !important;
    padding: 15px;
    order: ${props => props.$position}; /* En móvil mantener orden natural 1-2-3 */
    
    &:hover {
      transform: none;
      box-shadow: 0 4px 15px ${props => (props.$position === 1 ? props.theme.gold : props.$position === 2 ? props.theme.silver : props.theme.bronze)};
    }
  }
`;

const PilotImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 15px;
  border: 3px solid ${props => props.theme.pilotImageBorder};
  background-color: ${props => props.theme.avatarBackground};
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 100px;
    height: 100px;
    margin-bottom: 12px;
  }
`;

const FirstPlacePilotImage = styled(PilotImage)`
  width: 180px;
  height: 180px;
  margin-bottom: 25px;
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 120px;
    height: 120px;
    margin-bottom: 15px;
  }
`;

const PilotNameLink = styled(Link)`
  font-size: 1.3em;
  font-weight: bold;
  color: ${props => props.theme.textLink};
  text-decoration: none;
  margin-bottom: 8px;
  
  &:hover {
    text-decoration: underline;
    color: ${props => props.theme.textLink};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.1em;
    margin-bottom: 6px;
  }
`;

const PilotPoints = styled.p`
  font-size: 1em;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 5px;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.9em;
    margin-bottom: 4px;
  }
`;

const PilotTeam = styled.p`
  font-size: 0.9em;
  color: ${props => props.theme.textMuted}; 
  margin-bottom: 8px;
  font-style: italic;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.8em;
    margin-bottom: 6px;
  }
`;

const PilotStats = styled.div`
  font-size: 0.85em; 
  color: ${props => props.theme.textStats};
  margin-top: 8px;
  line-height: 1.4;
  width: 100%;
  background-color: #0a0c19;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.8em;
    margin-top: 6px;
    line-height: 1.3;
  }
`;

const StatItem = styled.span`
  display: inline-block; 
  margin: 2px 5px; 
  padding: 3px 6px;
  background-color: ${props => props.theme.statsBackground};
  color: ${props => props.theme.textStats};
  border-radius: 4px;
  font-size: 0.9em;

  &[title="Ver Historial"] {
    cursor: pointer;
    &:hover {
      background-color: ${props => props.theme.buttonBackground};
      color: ${props => props.theme.buttonText};
    }
  }
`;

const TeamLogoPodium = styled.img`
  width: 50px;
  height: 40px;
  object-fit: contain;
  margin-bottom: 8px;
  border-radius: 4px; 
  // border: 1px solid ${props => props.theme.podiumBorder}; // Optional border
`;

const RestOfClassificationContainer = styled.div`
  margin-top: 40px;
  padding: 0 20px; 
  background-color: #0a0c19;
  
  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 20px;
    padding: 0 5px;
    background-color: #0a0c19;
    width: 100%;
    box-sizing: border-box;
  }
`;

const ClassificationTitle = styled.h2`
  font-size: 2rem;
  color: ${props => props.theme.listTitleColor};
  text-align: center;
  margin-bottom: 30px;
  font-weight: bold;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.5rem;
    margin-bottom: 20px;
    padding: 0 10px;
  }
`;

const PilotListItemContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${props => props.theme.listItemBackground};
  padding: 15px 20px;
  margin-bottom: 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.listItemBorder};
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 10px rgba(0,0,0,0.15);
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    position: relative;
    flex-direction: column;
    align-items: flex-start;
    padding: 45px 15px 15px 15px;
    margin-bottom: 10px;
    
    &:hover {
      transform: none;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    }
  }
`;

const PilotListPosition = styled.span`
  font-size: 1.2em;
  font-weight: bold;
  color: ${props => props.theme.listTextPrimary};
  margin-right: 15px;
  width: 30px; 
  text-align: center;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.1em;
    margin-right: 10px;
    width: 25px;
    position: absolute;
    left: 15px;
    top: 15px;
  }
`;

const PilotListAvatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 15px;
  border: 2px solid ${props => props.theme.listAvatarBorder};
  background-color: ${props => props.theme.avatarBackground};
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 45px;
    height: 45px;
    margin-right: 12px;
    margin-left: 35px;
  }
`;

const TeamLogoList = styled.img`
  width: 30px;
  height: 30px;
  object-fit: contain;
  margin-right: 15px;
  border-radius: 4px; 
  // border: 1px solid ${props => props.theme.listItemBorder}; // Opcional
`;

const PilotListInfo = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    margin-bottom: 10px;
  }
`;

const PilotListNameLink = styled(Link)`
  font-size: 1.1em;
  font-weight: 500;
  color: ${props => props.theme.textLink}; // Usamos el textLink general
  text-decoration: none;
  margin-bottom: 3px;
  &:hover {
    text-decoration: underline;
  }
`;

const PilotListTeam = styled.span`
  font-size: 0.9em;
  color: ${props => props.theme.listTextSecondary};
  font-style: italic;
`;

const PilotListStats = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: ${props => props.theme.textStats};
  font-size: 0.9em;
  min-width: 220px; 
  justify-content: flex-end; 
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    min-width: auto;
    justify-content: flex-start;
    margin-top: 8px;
    font-size: 0.85em;
    gap: 6px;
  }
`;

const PilotListPoints = styled.span`
  font-size: 1.1em;
  font-weight: bold;
  color: ${props => props.theme.listTextPrimary};
  margin-right: 20px;
  min-width: 80px;
  text-align: right;
  
  @media (max-width: ${breakpoints.mobile}) {
    position: absolute;
    right: 15px;
    top: 15px;
    margin-right: 0;
    min-width: auto;
    font-size: 1em;
  }
`;

const LoadingText = styled.p`
  font-size: 1.5rem;
  text-align: center;
  color: #fff;
`;

const ExpandButton = styled.button`
  background: none;
  border: 1px solid ${theme.colors.secondary.light};
  color: ${theme.colors.secondary.light};
  padding: 3px 8px;
  margin-left: auto; 
  margin-right: 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8em;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: ${theme.colors.secondary.light};
    color: ${theme.colors.primary.main};
  }
`;

const HistoryContainer = styled.div`
  padding: 10px 15px 15px 55px; 
  background-color: rgba(0, 0, 0, 0.1);
  border-top: 1px solid rgba(255,255,255,0.1);
`;

const HistoryItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 0.9em;
  color: #ddd;

  &:not(:last-child) {
    border-bottom: 1px dashed rgba(255,255,255,0.05);
  }
`;

const HistoryRaceName = styled.span`
  color: #bbb;
`;

const HistoryPosition = styled.span`
  font-weight: bold;
  color: #fff;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.theme.modalOverlayBackground};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 10px;
    align-items: flex-start;
    padding-top: 20px;
  }
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.modalContentBackground};
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.3);
  width: 90%;
  max-width: 1350px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid ${props => props.theme.modalBorder};
  color: ${props => props.theme.modalTextColor};
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 90%;
    max-width: 90vw;
    padding: 20px 15px;
    max-height: 80vh;
    border-radius: 8px;
    margin: 10px;
  }
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: ${props => props.theme.modalCloseButtonBackground};
  color: ${props => props.theme.modalCloseButtonColor};
  border: 2px solid ${props => props.theme.modalCloseButtonColor};
  font-size: 1.8rem;
  cursor: pointer;
  padding: 5px 10px;
  line-height: 1;
  border-radius: 5px;
  transition: background-color 0.2s, color 0.2s;
  z-index: 2001;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.theme.modalCloseButtonHoverBackground};
    color: ${props => props.theme.modalCloseButtonHoverColor};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    top: 10px;
    right: 10px;
    font-size: 2rem;
    min-width: 44px;
    min-height: 44px;
    padding: 8px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    border: 2px solid white;
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
  }
`;

const HistoryModalTitle = styled.h3`
  font-size: 1.8rem;
  color: ${props => props.theme.modalTitleColor};
  text-align: center;
  margin-bottom: 25px;
  font-weight: bold;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.3rem;
    margin-bottom: 20px;
    padding-right: 60px;
    margin-top: 10px;
  }
`;

const ModalGridLayout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr 1fr; 
  gap: 25px;
  width: 100%;
  
  @media (max-width: ${breakpoints.mobile}) {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
`;

// Layout vertical específico para el podio para evitar que se desborde horizontalmente
const PodiumModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: ${breakpoints.mobile}) {
    gap: 15px;
  }
`;

const PodiumRacesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  width: 100%;
  
  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const PilotInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-right: 20px;
  border-right: 1px solid ${props => props.theme.modalSectionBorder};
  
  @media (max-width: ${breakpoints.mobile}) {
    padding-right: 0;
    border-right: none;
    border-bottom: 1px solid ${props => props.theme.modalSectionBorder};
    padding-bottom: 20px;
  }
`;

const RacesColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const TeammatesListModal = styled.div`
  margin-top: 15px;
  h4 {
    color: ${props => props.theme.modalTitleColor};
    font-size: 1.1em;
    margin-bottom: 12px;
    border-bottom: 1px solid ${props => props.theme.modalSectionBorder};
    padding-bottom: 8px;
  }
`;

const TeammateItemModal = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  font-size: 0.95em;
  color: ${props => props.theme.modalTextColor};
  img {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid ${props => props.theme.listAvatarBorder};
    background-color: ${props => props.theme.avatarBackground};
  }
  span {
    opacity: 0.9;
  }
`;

const RaceCardContainer = styled.div`
  background-color: ${props => props.theme.raceCardBackground};
  border: 1px solid ${props => props.theme.raceCardBorder};
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden; 
`;

const RaceCardHeader = styled.div`
  background-color: ${props => props.theme.raceCardHeaderBackground};
  padding: 10px 15px;
  font-size: 1.1rem;
  font-weight: 500;
  color: ${props => props.theme.raceCardHeaderText};
  border-bottom: 1px solid ${props => props.theme.raceCardBorder};
`;

const RaceCardBody = styled.div`
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  color: ${props => props.theme.modalTextColor};
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 12px;
  }
`;

const RaceCardPosition = styled.span`
  font-weight: bold;
  font-size: 1.2rem;
  color: ${props => props.theme.modalTitleColor}; 
`;

const RaceCardPoints = styled.span`
  font-weight: 500;
`;

const RaceCardStats = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.modalSubtleTextColor};
  text-align: right;

  span {
    display: block;
    margin-bottom: 3px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const PilotAvatarModal = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${props => props.theme.pilotImageBorder};
  background-color: ${props => props.theme.avatarBackground};
`;

const ModalPilotName = styled.h3`
  font-size: 1.6rem;
  color: ${props => props.theme.modalTitleColor};
  margin: 0;
`;

const ModalTeamName = styled.div`
  display: flex;
  flex-direction: column; 
  align-items: center;    
  text-align: center;     
  width: 100%;            
  margin-top: 8px;      

  img {
    width: 100%;           
    max-width: 180px;     
    height: auto;         
    object-fit: contain;
    margin-bottom: 12px;   
    transition: transform 0.2s ease-in-out;
    
    &:hover {
      transform: scale(1.05);
    }
  }

  font-size: 0.95rem; 
  color: ${props => props.theme.modalSubtleTextColor};
`;

const ModalStats = styled.div`
  margin-top: 10px;
  font-size: 0.9rem;
  color: ${props => props.theme.modalSubtleTextColor};
`;

const PilotDetailsContainerModal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ModalSanctionsTitle = styled.h4`
  color: ${props => props.theme.modalTitleColor};
  font-size: 1.1em;
  margin-bottom: 12px;
  border-bottom: 1px solid ${props => props.theme.modalSectionBorder};
  padding-bottom: 8px;
`;

const SanctionDetailCard = styled.div`
  padding: 10px;
  border-bottom: 1px solid ${props => props.theme.modalSectionBorder};
  &:last-child {
    border-bottom: none;
  }
`;

const TeamInfoContainerModal = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  color: ${props => props.theme.modalSubtleTextColor};
  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
`;

const TeamLogoModal = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const TeamNameModal = styled.span`
  font-size: 1rem;
  color: ${props => props.theme.modalSubtleTextColor};
`;

const TeammatesSectionModal = styled.div`
  margin-top: 15px;
`;

const TeammatesHeaderModal = styled.h4`
  color: ${props => props.theme.modalTitleColor};
  font-size: 1.1em;
  margin-bottom: 12px;
  border-bottom: 1px solid ${props => props.theme.modalSectionBorder};
  padding-bottom: 8px;
`;

const StyledRaceColumnContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center; 
  gap: 10px; 
  height: 100%; 
`;

const RaceHistoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  width: 100%;
  flex-grow: 1; 
`;

const ExpandedDetailPanel = styled.div`
  background-color: ${props => props.theme.listItemBackground}; // Un poco más oscuro o diferenciado
  padding: 20px;
  margin: 0 10px 10px 10px; // Margen para separarlo y darle aspecto de panel
  border-radius: 0 0 8px 8px; // Bordes redondeados abajo
  border: 1px solid ${props => props.theme.listItemBorder};
  border-top: none; // El ListItemStyled ya tiene un borde inferior que actúa como superior para este
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);

  // Ajustes de tamaño de fuente para elementos dentro del panel expandido
  ${RaceCardHeader} {
    font-size: 1em; // Ligeramente más pequeño que el original (que es 1.2em por defecto para h4)
    margin-bottom: 5px;
  }

  ${RaceCardBody} {
    padding: 8px; // Reducir padding si es necesario
  }

  ${RaceCardPosition} {
    font-size: 0.85em;
    margin-bottom: 3px;
  }

  ${RaceCardPoints} {
    font-size: 0.85em;
  }
`;

// Styled components para el LastreInfo responsive en modal
const ModalPilotInfoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 15px;
  background: ${props => props.theme.raceCardBackground};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.raceCardBorder};
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    gap: 15px;
  }
`;

const ModalPilotDataContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  min-width: 0; // Permite que el contenido se comprima
`;

const ModalPilotDetails = styled.div`
  flex: 1;
  min-width: 0; // Permite que el contenido se comprima
`;

const ModalLastreContainer = styled.div`
  min-width: 250px;
  flex-shrink: 0;
  
  @media (max-width: ${breakpoints.mobile}) {
    min-width: 0;
    width: 100%;
    max-width: 100%;
  }
`;

const ClasificacionGamerPage: React.FC = () => {
  const { ligaId } = useParams<{ ligaId: string }>();
  const { data, loading } = useContext(AppContext);
  const [liga, setLiga] = useState<Liga | null>(null);
  const [resultadosCalculados, setResultadosCalculados] = useState<ResultadoPilotoCalculado[]>([]);
  const [selectedPilotForHistory, setSelectedPilotForHistory] = useState<ResultadoPilotoCalculado | null>(null);
  const [expandedPilotId, setExpandedPilotId] = useState<string | null>(null);
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>('dark');


  const handlePilotClick = (piloto: ResultadoPilotoCalculado) => {
    //console.log('[ClasificacionGamerPage] Piloto seleccionado:', piloto);
    //console.log('[ClasificacionGamerPage] Lastres calculados para este piloto:', lastresCalculados[piloto.socioId]);
    setSelectedPilotForHistory(piloto);
    setExpandedPilotId(prevId => prevId === piloto.socioId ? null : piloto.socioId);
  };

  const toggleTheme = () => {
    setThemeMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  const handleCloseModal = () => {
    setExpandedPilotId(null);
    setSelectedPilotForHistory(null);
  };

  // Función para determinar si un piloto está en el podio (top 3)
  const isPodiumPilot = (piloto: ResultadoPilotoCalculado) => {
    const pilotIndex = resultadosCalculados.findIndex(p => p.socioId === piloto.socioId);
    return pilotIndex >= 0 && pilotIndex <= 2;
  };

  const currentTheme = themeMode === 'light' ? lightThemeColors : darkThemeColors;
  
  // Calcular lastres usando useMemo para evitar recálculos innecesarios
  const lastresCalculados = useMemo(() => {
    if (!liga || !data) return {};
    
    // Filtrar carreras de la liga
    const carrerasDeLaLiga = data.carreras.filter(c => c.ligaId === ligaId && c.publicada);
    
    // Filtrar socios por categoría
    const categoriaLigaNormalizada = liga.categoria?.trim().toLowerCase() || '';
    const sociosFiltrados = data.socios.filter(socio => {
      const socioCategoriaNormalizada = socio.Categoria?.trim().toLowerCase() || '';
      return socioCategoriaNormalizada === categoriaLigaNormalizada;
    });
    
    // Calcular resultados de equipos por carrera
    const equiposResultados: { [carreraId: string]: { [equipo: string]: number } } = {};
    
    carrerasDeLaLiga.forEach(carrera => {
      const puntosPorEquipo: { [equipo: string]: number } = {};
      
      // Agrupar los resultados de la carrera por equipo
      carrera.resultados?.forEach(resultado => {
        const piloto = data.socios.find(s => s.id === resultado.socioId);
        if (piloto && piloto.EQUIPO) {
          if (!puntosPorEquipo[piloto.EQUIPO]) {
            puntosPorEquipo[piloto.EQUIPO] = 0;
          }
          puntosPorEquipo[piloto.EQUIPO] += resultado.puntos;
        }
      });
      
      // Ordenar equipos por puntos y asignar posiciones
      const equiposOrdenados = Object.entries(puntosPorEquipo)
        .sort(([, puntosA], [, puntosB]) => puntosB - puntosA)
        .map(([equipo], index) => ({ equipo, posicion: index + 1 }));
      
      // Crear mapa de posiciones por equipo
      const posicionesPorEquipo: { [equipo: string]: number } = {};
      equiposOrdenados.forEach(({ equipo, posicion }) => {
        posicionesPorEquipo[equipo] = posicion;
      });
      
      equiposResultados[carrera.id] = posicionesPorEquipo;
    });
    
    // Obtener sanciones de la liga actual
    const sancionesData = data.sanciones?.filter(s => 
      carrerasDeLaLiga.some(c => c.id === s.carreraId)
    ) || [];
    
    // Calcular lastres
    return calcularLastresLiga({
      liga,
      carreras: carrerasDeLaLiga,
      socios: sociosFiltrados,
      resultados: carrerasDeLaLiga.flatMap(c => c.resultados || []),
      equiposResultados,
      sanciones: sancionesData // Pasar las sanciones al calculador
    });
  }, [liga, ligaId, data]);

  useEffect(() => {
    //console.log('[ClasificacionGamerPage] useEffect triggered. LigaID:', ligaId, 'Loading:', loading, 'Data available:', !!data);
    if (data && ligaId) {
      const ligaActual = data.ligas.find(l => l.id === ligaId) || null;
      //console.log('[ClasificacionGamerPage] Liga Actual:', ligaActual);
      setLiga(ligaActual);

      if (ligaActual) {
        //console.log('[ClasificacionGamerPage] Categoria de la Liga Actual:', ligaActual.categoria);
        const carrerasDeLaLiga = data.carreras.filter(c => c.ligaId === ligaId && c.publicada);
        //console.log('[ClasificacionGamerPage] Carreras de la liga:', carrerasDeLaLiga);
        const sancionesData = data.sanciones || [];
        //console.log('[ClasificacionGamerPage] Sanciones Data:', sancionesData);

        // Calcular resultados de equipos por carrera para el cálculo de lastres
        const equiposResultadosTemp: { [carreraId: string]: { [equipo: string]: number } } = {};

        // Primero, calcular los puntos por equipo en cada carrera
        carrerasDeLaLiga.forEach(carrera => {
          const puntosPorEquipo: { [equipo: string]: number } = {};
          
          // Agrupar los resultados de la carrera por equipo
          carrera.resultados.forEach(resultado => {
            const piloto = data.socios.find(s => s.id === resultado.socioId);
            if (piloto && piloto.EQUIPO) {
              if (!puntosPorEquipo[piloto.EQUIPO]) {
                puntosPorEquipo[piloto.EQUIPO] = 0;
              }
              puntosPorEquipo[piloto.EQUIPO] += resultado.puntos;
            }
          });
          
          // Ordenar equipos por puntos y asignar posiciones
          const equiposOrdenados = Object.entries(puntosPorEquipo)
            .sort(([, puntosA], [, puntosB]) => puntosB - puntosA)
            .map(([equipo], index) => ({ equipo, posicion: index + 1 }));
          
          // Crear mapa de posiciones por equipo
          const posicionesPorEquipo: { [equipo: string]: number } = {};
          equiposOrdenados.forEach(({ equipo, posicion }) => {
            posicionesPorEquipo[equipo] = posicion;
          });
          
          equiposResultadosTemp[carrera.id] = posicionesPorEquipo;
        });

        //console.log('[ClasificacionGamerPage] Resultados de equipos calculados:', equiposResultadosTemp);
        
        // El cálculo de lastres ahora se realiza en el useMemo

        const categoriaLigaNormalizada = ligaActual.categoria?.trim().toLowerCase() || '';
        //console.log('[ClasificacionGamerPage] Categoria de la Liga Actual (Normalizada):', categoriaLigaNormalizada);

        const sociosFiltrados = data.socios.filter(socio => {
          const socioCategoriaOriginal = socio.Categoria; 
          const socioCategoriaNormalizada = socio.Categoria?.trim().toLowerCase() || '';
          //console.log(`[ClasificacionGamerPage] Filtrando socio: ${socio.nombre} ${socio.apellido}, Socio Categoria (Original): '${socioCategoriaOriginal}', Socio Categoria (Norm): '${socioCategoriaNormalizada}', Categoria Liga (Norm): '${categoriaLigaNormalizada}'`);
          return socioCategoriaNormalizada === categoriaLigaNormalizada;
        });
        //console.log('[ClasificacionGamerPage] Socios Filtrados (usando socio.Categoria):', sociosFiltrados);
        
        // El cálculo de lastres ahora se realiza en el useMemo

        const pilotosCalculados = sociosFiltrados
          .map(socio => {
            let puntosTotales = 0;
            let victorias = 0;
            let segundosPuestos = 0;
            let tercerosPuestos = 0;
            let poles = 0;
            let vueltasRapidas = 0;
            let carrerasParticipadas = 0;
            let totalPuntosSancion = 0;
            const historialCarrerasSocio: { carreraId: string; nombreCarrera: string; posicion: number; puntosObtenidos?: number; hizoPole?: boolean; hizoVueltaRapida?: boolean }[] = [];

            // Primero, sumamos todos los puntos de las carreras
            carrerasDeLaLiga.forEach(carrera => {
              const resultadoDeSocioEnCarrera = carrera.resultados?.find((r: ResultadoCarrera) => r.socioId === socio.id);
              
              if (resultadoDeSocioEnCarrera) {
                carrerasParticipadas++;
                
                // Aplicar la misma lógica que en ClasificacionDetail.tsx
                // Si es DNF, siempre 1 punto de consolación independientemente de los puntos asignados
                // Si no se ha presentado, siempre 0 puntos
                let puntosObtenidos = 0;
                
                if (resultadoDeSocioEnCarrera.noParticipacion) {
                  // No se presentó, siempre 0 puntos
                  puntosObtenidos = 0;
                } else if (resultadoDeSocioEnCarrera.dnf) {
                  // DNF: 1 punto de consolación siempre
                  puntosObtenidos = 1;
                  // Añadir sanciones positivas si existen
                  const sancionesPiloto = data.sanciones?.filter(s => 
                    s.carreraId === carrera.id && s.socioId === socio.id
                  ) || [];
                  const puntosSanciones = sancionesPiloto.reduce((total, sancion) => total + (sancion.puntosPenalizacion || 0), 0);
                  if (puntosSanciones > 0) {
                    puntosObtenidos += puntosSanciones;
                  }
                } else {
                  // Normal: usar los puntos asignados
                  puntosObtenidos = resultadoDeSocioEnCarrera.puntos || 0;
                }
                
                console.log(`[DEBUG] Piloto: ${socio.display_name}, Carrera: ${carrera.nombre}, Puntos obtenidos: ${puntosObtenidos}, DNF: ${resultadoDeSocioEnCarrera.dnf}, Puntos originales: ${resultadoDeSocioEnCarrera.puntos}`);
                puntosTotales += puntosObtenidos;

                // Solo contar victorias y podios si no es DNF
                if (!resultadoDeSocioEnCarrera.dnf) {
                  if (resultadoDeSocioEnCarrera.posicion === 1) victorias++;
                  if (resultadoDeSocioEnCarrera.posicion === 2) segundosPuestos++;
                  if (resultadoDeSocioEnCarrera.posicion === 3) tercerosPuestos++;
                }
                
                // Aún se cuentan poles y vueltas rápidas aunque sea DNF
                if (resultadoDeSocioEnCarrera.pole) poles++;
                if (resultadoDeSocioEnCarrera.vueltaRapida) vueltasRapidas++;

                historialCarrerasSocio.push({
                  carreraId: carrera.id,
                  nombreCarrera: carrera.nombre,
                  posicion: resultadoDeSocioEnCarrera.posicion,
                  puntosObtenidos: puntosObtenidos, // Usar los puntos ya ajustados para DNF
                  hizoPole: resultadoDeSocioEnCarrera.pole,
                  hizoVueltaRapida: resultadoDeSocioEnCarrera.vueltaRapida
                });
              }
            });

            // Luego, restamos las sanciones
            carrerasDeLaLiga.forEach(carrera => {
              const sancionesDeLaCarreraParaPiloto = sancionesData.filter(
                s => s.carreraId === carrera.id && s.socioId === socio.id
              );
              
              sancionesDeLaCarreraParaPiloto.forEach(sancion => {
                console.log(`[DEBUG] Aplicando sanción a ${socio.display_name} en ${carrera.nombre}: ${sancion.puntosPenalizacion} puntos`);
                // Sumamos el valor absoluto para asegurarnos de que siempre restamos el valor correcto
                totalPuntosSancion += Math.abs(sancion.puntosPenalizacion);
              });
            });
            
            console.log(`[DEBUG] Piloto: ${socio.display_name}, Puntos antes de sanciones: ${puntosTotales}`);
            console.log(`[DEBUG] Total de puntos de sanción: ${totalPuntosSancion}`);
            
            // Restamos las sanciones (siempre restamos el valor absoluto)
            puntosTotales = Math.max(0, puntosTotales - totalPuntosSancion);
            console.log(`[DEBUG] Puntos después de aplicar sanciones: ${puntosTotales}`);

            const equipo = socio.EQUIPO ? data.equipos?.find(e => e.nombre === socio.EQUIPO) : undefined;
            const resultadoPilotoLog = {
              socioId: socio.id,
              nombre: socio.nombre, 
              apellido: socio.apellido, 
              display_name_socio: socio.display_name, 
              fotoPerfil: socio.fotoPerfil, 
              equipoNombre: equipo?.nombre,
              equipoId: equipo?.id,
              // Usar imagenCoche primero, luego logoUrl como fallback
              equipoLogoUrl: equipo?.imagenCoche || equipo?.logoUrl, 
              puntosTotales,
              victorias,
              segundosPuestos,
              tercerosPuestos,
              poles,
              vueltasRapidas,
              carrerasParticipadas,
              sancionesPuntosTotales: totalPuntosSancion,
            };
            //console.log('[ClasificacionGamerPage] Calculando para piloto:', resultadoPilotoLog);
            return {
              socioId: socio.id,
              nombreCompleto: socio.display_name || 'Nombre no disponible', 
              fotoUrl: socio.fotoUrl || socio.fotoPerfil,
              equipoId: equipo?.id,
              equipoNombre: equipo?.nombre || 'Sin equipo',
              equipoLogoUrl: equipo?.imagenCoche || equipo?.logoUrl,
              puntosTotales,
              victorias,
              segundosPuestos,
              tercerosPuestos,
              poles,
              vueltasRapidas,
              carrerasParticipadas,
              sancionesPuntosTotales: totalPuntosSancion,
              puntosLicencia: socio.puntosLicencia !== undefined ? socio.puntosLicencia : 'N/A',
              nivelReputacion: socio.nivelReputacion !== undefined ? socio.nivelReputacion : 'N/A',
              lastre: lastresCalculados[socio.id] || { kgTotal: 0, detalle: ['No hay datos de lastre disponibles'] },
              historialCarrerasLiga: historialCarrerasSocio
            };
          })
          // Mostrar todos los pilotos de la liga, independientemente de si han participado o no
          .sort((a, b) => {
            if (b.puntosTotales !== a.puntosTotales) {
              return b.puntosTotales - a.puntosTotales;
            }
            if (b.victorias !== a.victorias) {
              return b.victorias - a.victorias;
            }
            if (b.segundosPuestos !== a.segundosPuestos) {
              return b.segundosPuestos - a.segundosPuestos;
            }
            if (b.tercerosPuestos !== a.tercerosPuestos) {
              return b.tercerosPuestos - a.tercerosPuestos;
            }
            return a.nombreCompleto.localeCompare(b.nombreCompleto); 
          });

        //console.log('[ClasificacionGamerPage] Pilotos Calculados (antes de setear estado):', pilotosCalculados);
        setResultadosCalculados(pilotosCalculados);
      }
    } else {
      //console.log('[ClasificacionGamerPage] useEffect: No data or no ligaId. Data:', data, 'LigaID:', ligaId);
    }
  }, [ligaId, data, loading]);

  //console.log('[ClasificacionGamerPage] Render - Loading:', loading, 'Liga:', liga, 'Resultados Calculados:', resultadosCalculados);

  if (loading || !data || !liga) { 
    let message = 'Cargando datos de la clasificación gamer...';
    if (!loading && (!data || !liga)) message = 'No se encontraron datos para esta liga o la liga no existe.';
    return <PageContainer><LoadingText>{message}</LoadingText></PageContainer>;
  }

  const restoClasificacion = resultadosCalculados.slice(3);

  return (
    <ThemeProvider theme={currentTheme}> 
      <PageContainer>
        {/* Temporarily hidden theme toggle button
        <ThemeToggleButton onClick={toggleTheme}>
          Cambiar a Tema {themeMode === 'light' ? 'Oscuro' : 'Claro'}
        </ThemeToggleButton><br></br>
        */}
          <Title>Clasificación  {liga?.nombre}</Title>
        
        {resultadosCalculados.length > 0 && (
          <PodiumContainer>
            {/* PRIMER PUESTO */}
            {resultadosCalculados.length > 0 && resultadosCalculados[0] && (
              <PodiumStep $position={1} key={`podium-${resultadosCalculados[0].socioId}`}>
                {resultadosCalculados[0].equipoLogoUrl && <TeamLogoPodium src={resultadosCalculados[0].equipoLogoUrl} alt={resultadosCalculados[0].equipoNombre} onError={(e) => (e.currentTarget.style.display = 'none')} />}
                <FirstPlacePilotImage 
                  src={resultadosCalculados[0].fotoUrl || generateInitialsAvatar(resultadosCalculados[0].nombreCompleto)} 
                  alt={resultadosCalculados[0].nombreCompleto} 
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    const initialsAvatar = generateInitialsAvatar(resultadosCalculados[0].nombreCompleto);
                    if (target.src !== initialsAvatar) { target.src = initialsAvatar; }
                  }}
                />
                <PilotNameLink to={`/socios/profile/${resultadosCalculados[0].socioId}`}>{resultadosCalculados[0].nombreCompleto}</PilotNameLink>
                <PilotPoints>
                  {resultadosCalculados[0].puntosTotales} Puntos
                  {lastresCalculados && lastresCalculados[resultadosCalculados[0].socioId] && lastresCalculados[resultadosCalculados[0].socioId].kgTotal > 0 && (
                    <span style={{ color: '#e4312e', marginLeft: '5px' }}>+{lastresCalculados[resultadosCalculados[0].socioId].kgTotal} Kg</span>
                  )}
                </PilotPoints>
                <PilotTeam>{resultadosCalculados[0].equipoNombre}</PilotTeam>
                <PilotStats>
                  <StatItem>🏆 {resultadosCalculados[0].victorias}</StatItem>
                  <StatItem>🥈 {resultadosCalculados[0].segundosPuestos}</StatItem>
                  <StatItem>🥉 {resultadosCalculados[0].tercerosPuestos}</StatItem>
                  <StatItem>🅿️ {resultadosCalculados[0].poles}</StatItem>
                  <StatItem>💨 {resultadosCalculados[0].vueltasRapidas}</StatItem>
                  <StatItem>🏎️ {resultadosCalculados[0].carrerasParticipadas}</StatItem>
                  {resultadosCalculados[0].sancionesPuntosTotales !== 0 && <StatItem title={`Puntos de Penalización: ${resultadosCalculados[0].sancionesPuntosTotales}`}>⚠️ {resultadosCalculados[0].sancionesPuntosTotales} Pen.Pts</StatItem>}
                  {resultadosCalculados[0].puntosLicencia !== 'N/A' && <StatItem>Lic: {resultadosCalculados[0].puntosLicencia}</StatItem>}
                  {resultadosCalculados[0].nivelReputacion !== 'N/A' && <StatItem>Rep: {resultadosCalculados[0].nivelReputacion}</StatItem>}
                  <StatItem onClick={() => handlePilotClick(resultadosCalculados[0])} style={{ cursor: 'pointer' }} title="Ver Historial">🗓️</StatItem>
                </PilotStats>
              </PodiumStep>
            )}
            
            {/* SEGUNDO PUESTO */}
            {resultadosCalculados.length > 1 && resultadosCalculados[1] && (
              <PodiumStep $position={2} key={`podium-${resultadosCalculados[1].socioId}`}>
                {resultadosCalculados[1].equipoLogoUrl && <TeamLogoPodium src={resultadosCalculados[1].equipoLogoUrl} alt={resultadosCalculados[1].equipoNombre} onError={(e) => (e.currentTarget.style.display = 'none')} />}
                <PilotImage 
                  src={resultadosCalculados[1].fotoUrl || generateInitialsAvatar(resultadosCalculados[1].nombreCompleto)} 
                  alt={resultadosCalculados[1].nombreCompleto} 
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    const initialsAvatar = generateInitialsAvatar(resultadosCalculados[1].nombreCompleto);
                    if (target.src !== initialsAvatar) { target.src = initialsAvatar; }
                  }}
                />
                <PilotNameLink to={`/socios/profile/${resultadosCalculados[1].socioId}`}>{resultadosCalculados[1].nombreCompleto}</PilotNameLink>
                <PilotPoints>
                  {resultadosCalculados[1].puntosTotales} Puntos
                  {lastresCalculados && lastresCalculados[resultadosCalculados[1].socioId] && lastresCalculados[resultadosCalculados[1].socioId].kgTotal > 0 && (
                    <span style={{ color: '#e4312e', marginLeft: '5px' }}>+{lastresCalculados[resultadosCalculados[1].socioId].kgTotal} Kg</span>
                  )}
                </PilotPoints>
                <PilotTeam>{resultadosCalculados[1].equipoNombre}</PilotTeam>
                <PilotStats>
                  <StatItem>🏆 {resultadosCalculados[1].victorias}</StatItem>
                  <StatItem>🥈 {resultadosCalculados[1].segundosPuestos}</StatItem>
                  <StatItem>🥉 {resultadosCalculados[1].tercerosPuestos}</StatItem>
                  <StatItem>🅿️ {resultadosCalculados[1].poles}</StatItem>
                  <StatItem>💨 {resultadosCalculados[1].vueltasRapidas}</StatItem>
                  <StatItem>🏎️ {resultadosCalculados[1].carrerasParticipadas}</StatItem>
                  {resultadosCalculados[1].sancionesPuntosTotales !== 0 && <StatItem title={`Puntos de Penalización: ${resultadosCalculados[1].sancionesPuntosTotales}`}>⚠️ {resultadosCalculados[1].sancionesPuntosTotales} Pen.Pts</StatItem>}
                  {resultadosCalculados[1].puntosLicencia !== 'N/A' && <StatItem>Lic: {resultadosCalculados[1].puntosLicencia}</StatItem>}
                  {resultadosCalculados[1].nivelReputacion !== 'N/A' && <StatItem>Rep: {resultadosCalculados[1].nivelReputacion}</StatItem>}
                  <StatItem onClick={() => handlePilotClick(resultadosCalculados[1])} style={{ cursor: 'pointer' }} title="Ver Historial">🗓️</StatItem>
                </PilotStats>
              </PodiumStep>
            )}
            
            {/* TERCER PUESTO */}
            {resultadosCalculados.length > 2 && resultadosCalculados[2] && (
              <PodiumStep $position={3} key={`podium-${resultadosCalculados[2].socioId}`}>
                {resultadosCalculados[2].equipoLogoUrl && <TeamLogoPodium src={resultadosCalculados[2].equipoLogoUrl} alt={resultadosCalculados[2].equipoNombre} onError={(e) => (e.currentTarget.style.display = 'none')} />}
                <PilotImage 
                  src={resultadosCalculados[2].fotoUrl || generateInitialsAvatar(resultadosCalculados[2].nombreCompleto)} 
                  alt={resultadosCalculados[2].nombreCompleto} 
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    const initialsAvatar = generateInitialsAvatar(resultadosCalculados[2].nombreCompleto);
                    if (target.src !== initialsAvatar) { target.src = initialsAvatar; }
                  }}
                />
                <PilotNameLink to={`/socios/profile/${resultadosCalculados[2].socioId}`}>{resultadosCalculados[2].nombreCompleto}</PilotNameLink>
                <PilotPoints>
                  {resultadosCalculados[2].puntosTotales} Puntos
                  {lastresCalculados && lastresCalculados[resultadosCalculados[2].socioId] && lastresCalculados[resultadosCalculados[2].socioId].kgTotal > 0 && (
                    <span style={{ color: '#e4312e', marginLeft: '5px' }}>+{lastresCalculados[resultadosCalculados[2].socioId].kgTotal} Kg</span>
                  )}
                </PilotPoints>
                <PilotTeam>{resultadosCalculados[2].equipoNombre}</PilotTeam>
                <PilotStats>
                  <StatItem>🏆 {resultadosCalculados[2].victorias}</StatItem>
                  <StatItem>🥈 {resultadosCalculados[2].segundosPuestos}</StatItem>
                  <StatItem>🥉 {resultadosCalculados[2].tercerosPuestos}</StatItem>
                  <StatItem>🅿️ {resultadosCalculados[2].poles}</StatItem>
                  <StatItem>💨 {resultadosCalculados[2].vueltasRapidas}</StatItem>
                  <StatItem>🏎️ {resultadosCalculados[2].carrerasParticipadas}</StatItem>
                  {resultadosCalculados[2].sancionesPuntosTotales !== 0 && <StatItem title={`Puntos de Penalización: ${resultadosCalculados[2].sancionesPuntosTotales}`}>⚠️ {resultadosCalculados[2].sancionesPuntosTotales} Pen.Pts</StatItem>}
                  {resultadosCalculados[2].puntosLicencia !== 'N/A' && <StatItem>Lic: {resultadosCalculados[2].puntosLicencia}</StatItem>}
                  {resultadosCalculados[2].nivelReputacion !== 'N/A' && <StatItem>Rep: {resultadosCalculados[2].nivelReputacion}</StatItem>}
                  <StatItem onClick={() => handlePilotClick(resultadosCalculados[2])} style={{ cursor: 'pointer' }} title="Ver Historial">🗓️</StatItem>
                </PilotStats>
              </PodiumStep>
            )}
          </PodiumContainer>
        )}
        {restoClasificacion.length > 0 && (
          <RestOfClassificationContainer>
            <ClassificationTitle>Resto de la Clasificación</ClassificationTitle>
            {restoClasificacion.map((piloto, index, array) => {
              // Determinar si el piloto actual está empatado con el anterior
              // Solo consideramos empate por puntos totales, ya que las demás estadísticas 
              // son criterios de desempate pero no cambian la posición mostrada
              const isTiedWithPrevious = index > 0 && 
                piloto.puntosTotales === array[index - 1].puntosTotales;
              
              // Si está empatado con el anterior, usar la misma posición (vacía)
              const position = isTiedWithPrevious ? '' : `${index + 4}°`;
               
              return (
                <React.Fragment key={piloto.socioId}>
                  <PilotListItemContainer>
                    <PilotListPosition>{position}</PilotListPosition>
                    <PilotListAvatar 
                      src={piloto.fotoUrl || generateInitialsAvatar(piloto.nombreCompleto)}
                      alt={piloto.nombreCompleto} 
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        const initialsAvatar = generateInitialsAvatar(piloto.nombreCompleto);
                        if (target.src !== initialsAvatar) { // Prevent infinite loop if initials avatar itself fails
                          target.src = initialsAvatar;
                        }
                      }}
                    />
                    {piloto.equipoLogoUrl && <TeamLogoList src={piloto.equipoLogoUrl} alt={piloto.equipoNombre} onError={(e) => (e.currentTarget.style.display = 'none')} />}
                    <PilotListInfo>
                      <PilotListNameLink to={`/socios/profile/${piloto.socioId}`}>{piloto.nombreCompleto}</PilotListNameLink>
                      <PilotListTeam>{piloto.equipoNombre}</PilotListTeam>
                    </PilotListInfo>
                    <PilotListPoints>
                      {piloto.puntosTotales} Pts
                      {lastresCalculados && lastresCalculados[piloto.socioId] && lastresCalculados[piloto.socioId].kgTotal > 0 && (
                        <span style={{ color: '#e4312e', marginLeft: '5px' }}>+{lastresCalculados[piloto.socioId].kgTotal} Kg</span>
                      )}
                    </PilotListPoints>
                    <PilotListStats>
                      <StatItem>🏆{piloto.victorias}</StatItem>
                      <StatItem>🥈{piloto.segundosPuestos}</StatItem>
                      <StatItem>🥉{piloto.tercerosPuestos}</StatItem>
                      <StatItem>🅿️{piloto.poles}</StatItem>
                      <StatItem>💨{piloto.vueltasRapidas}</StatItem>
                      <StatItem>🏎️{piloto.carrerasParticipadas}</StatItem>
                      {piloto.sancionesPuntosTotales !== 0 && <StatItem title={`Puntos de Penalización: ${piloto.sancionesPuntosTotales}`}>⚠️{piloto.sancionesPuntosTotales}</StatItem>}
                      {piloto.puntosLicencia !== 'N/A' && <StatItem>Lic: {piloto.puntosLicencia}</StatItem>}
                      {piloto.nivelReputacion !== 'N/A' && <StatItem>Rep: {piloto.nivelReputacion}</StatItem>}
                      <StatItem onClick={() => handlePilotClick(piloto)} style={{ cursor: 'pointer' }} title="Ver Historial">🗓️</StatItem>
                    </PilotListStats>
                  </PilotListItemContainer>
                  {expandedPilotId === piloto.socioId && selectedPilotForHistory && !isPodiumPilot(piloto) && (
                    <ExpandedDetailPanel>
                      <ModalGridLayout>
                        <PilotInfoColumn>
                          <PilotAvatarModal 
                            src={selectedPilotForHistory.fotoUrl || generateInitialsAvatar(selectedPilotForHistory.nombreCompleto)} 
                            alt={selectedPilotForHistory.nombreCompleto} 
                            style={{ width: '100px', height: '100px', margin: '0 auto 15px auto', display: 'block' }}
                          />
                          <ModalPilotName style={{ textAlign: 'center' }}>{selectedPilotForHistory.nombreCompleto}</ModalPilotName>
                          {selectedPilotForHistory.equipoNombre && (
                            <ModalTeamName>
                              <img 
                                src={selectedPilotForHistory.equipoLogoUrl || defaultTeamLogo} 
                                alt={selectedPilotForHistory.equipoNombre} 
                                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => { 
                                  const target = e.currentTarget as HTMLImageElement;
                                  if (target.src !== defaultTeamLogo) {
                                    console.warn(`Error loading team logo (expanded): ${target.src}. Attempting fallback.`);
                                    target.src = defaultTeamLogo;
                                  } else {
                                    console.error(`Fallback team logo (defaultTeamLogo) also failed (expanded). Hiding image.`);
                                    target.style.display = 'none';
                                  }
                                }}
                              /> 
                              {selectedPilotForHistory.equipoNombre}
                            </ModalTeamName>
                          )}
                          
                          {lastresCalculados && lastresCalculados[selectedPilotForHistory.socioId] && (
                            <div style={{ marginTop: '15px' }}>
                              <LastreInfo 
                                kgTotal={lastresCalculados[selectedPilotForHistory.socioId].kgTotal} 
                                detalle={lastresCalculados[selectedPilotForHistory.socioId].detalle} 
                                darkMode={themeMode === 'dark'}
                              />
                            </div>
                          )}
                          
                          <TeammatesListModal>
                            <TeammatesHeaderModal>Compañeros de Equipo:</TeammatesHeaderModal>
                            {(() => {
                              const pilotTeamId = selectedPilotForHistory.equipoId;
                              if (!pilotTeamId) return <TeammateItemModal><span>No asignado a un equipo.</span></TeammateItemModal>;
                              
                              const currentTeammates = (resultadosCalculados || []).filter(
                                (p: ResultadoPilotoCalculado) => p.socioId !== selectedPilotForHistory.socioId && p.equipoId === pilotTeamId
                              );

                              if (currentTeammates.length === 0) return <TeammateItemModal><span>Sin compañeros en esta liga.</span></TeammateItemModal>;

                              return currentTeammates.map((t: ResultadoPilotoCalculado) => (
                                <TeammateItemModal key={`teammate-podium1-${t.socioId}`}>
                                  <img 
                                    src={t.fotoUrl || generateInitialsAvatar(t.nombreCompleto)} 
                                    alt={t.nombreCompleto} 
                                    onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                      const target = e.currentTarget as HTMLImageElement;
                                      const currentSrc = target.src; 
                                      const initialsSrc = generateInitialsAvatar(t.nombreCompleto);
                                      if (currentSrc !== initialsSrc && currentSrc !== defaultAvatar) {
                                        target.src = initialsSrc;
                                      } else if (currentSrc === initialsSrc && currentSrc !== defaultAvatar) {
                                        target.src = defaultAvatar;
                                      } else {
                                        target.style.display = 'none';
                                        target.onerror = null; 
                                      }
                                    }}
                                  />
                                  <span>{t.nombreCompleto}</span>
                                </TeammateItemModal>
                              ));
                            })()}
                          </TeammatesListModal>
                        </PilotInfoColumn>

                        {(() => {
                          const races = selectedPilotForHistory.historialCarrerasLiga || [];
                          const racesPerColumn = 3; 
                          const racesCol1 = races.slice(0, racesPerColumn);
                          const racesCol2 = races.slice(racesPerColumn, racesPerColumn * 2);

                          return (
                            <>
                              <RacesColumn>
                                <StyledRaceColumnContent>
                                  {racesCol1.length > 0 ? (
                                    racesCol1.map((carreraHist, index) => (
                                      <RaceCardContainer key={`${selectedPilotForHistory.socioId}-exp-col1-${index}-${carreraHist.carreraId}`}>
                                        <RaceCardHeader>{carreraHist.nombreCarrera}</RaceCardHeader>
                                        <RaceCardBody>
                                          <RaceCardPosition>Puesto: {carreraHist.posicion}</RaceCardPosition>
                                          <RaceCardPoints>{carreraHist.puntosObtenidos ?? 0} Pts</RaceCardPoints>
                                        </RaceCardBody>
                                        <RaceCardStats>
                                          {carreraHist.hizoPole && <span title="Pole Position">🅿️</span>}
                                          {carreraHist.hizoVueltaRapida && <span title="Vuelta Rápida">💨</span>}
                                        </RaceCardStats>
                                      </RaceCardContainer>
                                    ))
                                  ) : (
                                    <p>No hay carreras para mostrar.</p>
                                  )}
                                </StyledRaceColumnContent>
                              </RacesColumn>
                              <RacesColumn>
                                <StyledRaceColumnContent>
                                  {racesCol2.length > 0 ? (
                                    racesCol2.map((carreraHist, index) => (
                                      <RaceCardContainer key={`${selectedPilotForHistory.socioId}-exp-col2-${index}-${carreraHist.carreraId}`}>
                                        <RaceCardHeader>{carreraHist.nombreCarrera}</RaceCardHeader>
                                        <RaceCardBody>
                                          <RaceCardPosition>Puesto: {carreraHist.posicion}</RaceCardPosition>
                                          <RaceCardPoints>{carreraHist.puntosObtenidos ?? 0} Pts</RaceCardPoints>
                                        </RaceCardBody>
                                        <RaceCardStats>
                                          {carreraHist.hizoPole && <span title="Pole Position">🅿️</span>}
                                          {carreraHist.hizoVueltaRapida && <span title="Vuelta Rápida">💨</span>}
                                        </RaceCardStats>
                                      </RaceCardContainer>
                                    ))
                                  ) : (
                                    races.length > racesPerColumn && <p>No hay más carreras para mostrar.</p>
                                  )}
                                </StyledRaceColumnContent>
                              </RacesColumn>
                            </>
                          );
                        })()}
                      </ModalGridLayout>
                    </ExpandedDetailPanel>
                  )}
                </React.Fragment>
              );
            })}
          </RestOfClassificationContainer>
        )}

        {/* Modal Popup para pilotos del podio (Top 3) */}
        {selectedPilotForHistory && expandedPilotId && isPodiumPilot(selectedPilotForHistory) && (
          <ModalOverlay onClick={handleCloseModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalCloseButton onClick={handleCloseModal}>×</ModalCloseButton>
              <HistoryModalTitle>Historial de {selectedPilotForHistory.nombreCompleto}</HistoryModalTitle>
              <PodiumModalLayout>
                {/* Información del piloto en la parte superior */}
                <ModalPilotInfoContainer>
                  <ModalPilotDataContainer>
                    <PilotAvatarModal 
                      src={selectedPilotForHistory.fotoUrl || generateInitialsAvatar(selectedPilotForHistory.nombreCompleto)} 
                      alt={selectedPilotForHistory.nombreCompleto} 
                      style={{ width: '80px', height: '80px', flexShrink: 0 }}
                    />
                    <ModalPilotDetails>
                      <ModalPilotName style={{ margin: '0 0 8px 0', fontSize: '1.4rem' }}>{selectedPilotForHistory.nombreCompleto}</ModalPilotName>
                      {selectedPilotForHistory.equipoNombre && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: currentTheme.modalSubtleTextColor }}>
                          <img 
                            src={selectedPilotForHistory.equipoLogoUrl || defaultTeamLogo} 
                            alt={selectedPilotForHistory.equipoNombre}
                            style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => { 
                              const target = e.currentTarget as HTMLImageElement;
                              if (target.src !== defaultTeamLogo) {
                                target.src = defaultTeamLogo;
                              } else {
                                target.style.display = 'none';
                              }
                            }}
                          /> 
                          <span>{selectedPilotForHistory.equipoNombre}</span>
                        </div>
                      )}
                    </ModalPilotDetails>
                  </ModalPilotDataContainer>
                  
                  {/* Mostrar información de lastres */}
                  {lastresCalculados && lastresCalculados[selectedPilotForHistory.socioId] && (
                    <ModalLastreContainer>
                      <LastreInfo 
                        kgTotal={lastresCalculados[selectedPilotForHistory.socioId].kgTotal} 
                        detalle={lastresCalculados[selectedPilotForHistory.socioId].detalle} 
                        darkMode={themeMode === 'dark'}
                      />
                    </ModalLastreContainer>
                  )}
                </ModalPilotInfoContainer>

                {/* Compañeros de equipo */}
                <TeammatesListModal>
                  <TeammatesHeaderModal>Compañeros de Equipo:</TeammatesHeaderModal>
                  {(() => {
                    const pilotTeamId = selectedPilotForHistory.equipoId;
                    if (!pilotTeamId) return <TeammateItemModal><span>No asignado a un equipo.</span></TeammateItemModal>;
                    const currentTeammates = (resultadosCalculados || []).filter((p: ResultadoPilotoCalculado) => p.socioId !== selectedPilotForHistory.socioId && p.equipoId === pilotTeamId);
                    if (currentTeammates.length === 0) return <TeammateItemModal><span>Sin compañeros en esta liga.</span></TeammateItemModal>;
                    return currentTeammates.map((t: ResultadoPilotoCalculado) => (
                      <TeammateItemModal key={`teammate-popup-${t.socioId}`}>
                        <img 
                          src={t.fotoUrl || generateInitialsAvatar(t.nombreCompleto)} 
                          alt={t.nombreCompleto} 
                          onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                            const target = e.currentTarget as HTMLImageElement; 
                            const currentSrc = target.src; 
                            const initialsSrc = generateInitialsAvatar(t.nombreCompleto);
                            if (currentSrc !== initialsSrc && currentSrc !== defaultAvatar) { 
                              target.src = initialsSrc; 
                            } else if (currentSrc === initialsSrc && currentSrc !== defaultAvatar) { 
                              target.src = defaultAvatar; 
                            } else { 
                              target.style.display = 'none'; target.onerror = null;
                            }
                          }}
                        />
                        <span>{t.nombreCompleto}</span>
                      </TeammateItemModal>
                    ));
                  })()}
                </TeammatesListModal>

                {/* Historial de carreras en grid de 2 columnas */}
                {(() => { 
                  const races = selectedPilotForHistory.historialCarrerasLiga || []; 
                  const racesPerColumn = Math.ceil(races.length / 2);
                  const racesCol1 = races.slice(0, racesPerColumn); 
                  const racesCol2 = races.slice(racesPerColumn); 
                  return (
                    <PodiumRacesGrid>
                      <div>
                        {racesCol1.length > 0 ? (
                          racesCol1.map((carreraHist, index) => (
                            <RaceCardContainer key={`${selectedPilotForHistory.socioId}-popup-col1-${index}-${carreraHist.carreraId}`}>
                              <RaceCardHeader>{carreraHist.nombreCarrera}</RaceCardHeader>
                              <RaceCardBody>
                                <RaceCardPosition>Puesto: {carreraHist.posicion}</RaceCardPosition>
                                <RaceCardPoints>{carreraHist.puntosObtenidos ?? 0} Pts</RaceCardPoints>
                              </RaceCardBody>
                              <RaceCardStats>
                                {carreraHist.hizoPole && <span title="Pole Position">🅿️</span>}
                                {carreraHist.hizoVueltaRapida && <span title="Vuelta Rápida">💨</span>}
                              </RaceCardStats>
                            </RaceCardContainer>
                          ))
                        ) : (<p>No hay carreras.</p>)}
                      </div>
                      <div>
                        {racesCol2.length > 0 && (
                          racesCol2.map((carreraHist, index) => (
                            <RaceCardContainer key={`${selectedPilotForHistory.socioId}-popup-col2-${index}-${carreraHist.carreraId}`}>
                              <RaceCardHeader>{carreraHist.nombreCarrera}</RaceCardHeader>
                              <RaceCardBody>
                                <RaceCardPosition>Puesto: {carreraHist.posicion}</RaceCardPosition>
                                <RaceCardPoints>{carreraHist.puntosObtenidos ?? 0} Pts</RaceCardPoints>
                              </RaceCardBody>
                              <RaceCardStats>
                                {carreraHist.hizoPole && <span title="Pole Position">🅿️</span>}
                                {carreraHist.hizoVueltaRapida && <span title="Vuelta Rápida">💨</span>}
                              </RaceCardStats>
                            </RaceCardContainer>
                          ))
                        )}
                      </div>
                    </PodiumRacesGrid>
                  );
                })()}
              </PodiumModalLayout>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageContainer>
    </ThemeProvider>
  );
};

export default ClasificacionGamerPage;
